import {
  useId,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes
} from "react";

import {
  Grid,
  GridItem,
  type GridProps,
  type TextProps,
  type HTMLChakraProps
} from "@chakra-ui/react";

import { ChangeEvent as ReactChangeEvent } from "react";

import { Text, Box } from "../internal/exports";
import {
  resolveTestId,
  scopedTestIds,
  type TestIdProps
} from "../utils/test-ids";
import VeryHappy from "./icons/very-happy";
import Happy from "./icons/happy";
import Neutral from "./icons/neutral";
import UnHappy from "./icons/unhappy";
import VeryUnHappy from "./icons/very-unhappy";

type UseRadioProps = {
  value?: string | number;
  disabled?: boolean;
  isChecked?: boolean;
  name?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  [key: string]: unknown;
};

type RadioProps = HTMLChakraProps<"div"> & {
  value?: string;
  disabled?: boolean;
  isChecked?: boolean;
};

function useRadio(props: UseRadioProps = {}) {
  const { value, disabled, isChecked, name, onChange, ...rest } = props;
  const checked = Boolean(isChecked);
  const [focused, setFocused] = useState(false);

  const getInputProps = (
    inputProps: InputHTMLAttributes<HTMLInputElement> &
      Record<string, unknown> = {}
  ) => ({
    type: "radio" as const,
    name,
    value,
    checked,
    disabled: disabled,
    onChange,
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
    ...inputProps
  });

  const getRadioProps = (radioProps: Record<string, unknown> = {}) => ({
    "data-checked": checked ? "" : undefined,
    "data-disabled": disabled ? "" : undefined,
    "data-focus": focused ? "" : undefined,
    "aria-checked": checked,
    ...rest,
    ...radioProps
  });

  return {
    getInputProps,
    getRadioProps,
    state: { isChecked: checked, disabled: Boolean(disabled) }
  };
}

function useRadioGroup(props: {
  value?: string;
  defaultValue?: string;
  onChange?: (nextValue: string) => void;
  name?: string;
  disabled?: boolean;
}) {
  const generatedName = useId();
  const name = props.name ?? generatedName;

  const getRootProps = (rootProps: Record<string, unknown> = {}) => ({
    role: "radiogroup",
    ...rootProps
  });

  const getRadioProps = (radioProps: UseRadioProps = {}) => {
    const radioValue = radioProps.value != null ? String(radioProps.value) : "";
    const groupValue = props.value != null ? String(props.value) : "";
    const isChecked = groupValue === radioValue;
    const disabled = Boolean(props.disabled || radioProps.disabled);

    return {
      ...radioProps,
      name,
      value: radioValue,
      isChecked,
      disabled,
      onChange: (event: ChangeEvent<HTMLInputElement>) => {
        radioProps.onChange?.(event);
        props.onChange?.(event.target.value);
      }
    };
  };

  return {
    getRootProps,
    getRadioProps,
    value: props.value,
    name
  };
}

type RadioGroupProps = Record<string, unknown>;

interface EmojiRadioProps {
  disabled: boolean;
  handleChange?: (event: ReactChangeEvent<HTMLInputElement>) => void;
  value: string;
  label: string;
  emojiType: "VeryUnHappy" | "UnHappy" | "Neutral" | "Happy" | "VeryHappy";
  labelProps?: TextProps;
  radioProps?: RadioProps;
  emojiOptionDataTestId?: string;
}

const getEmoji = {
  VeryUnHappy,
  UnHappy,
  Neutral,
  Happy,
  VeryHappy
};

const EmojiIcon = ({
  emojiType
}: {
  emojiType: EmojiRadioProps["emojiType"];
}) => {
  const Component = getEmoji[emojiType];
  if (!Component) return null;
  return <Component boxSize="12" />;
};

const EmojiRadio = (props: EmojiRadioProps) => {
  const {
    handleChange,
    value,
    disabled,
    label,
    emojiType,
    labelProps,
    radioProps,
    emojiOptionDataTestId
  } = props;

  const { getInputProps, getRadioProps } = useRadio({
    value,
    disabled: disabled,
    onChange: handleChange,
    ...radioProps
  });

  return (
    <Box
      as="label"
      position="relative"
      display="block"
      cursor={disabled ? "not-allowed" : "pointer"}
    >
      <input
        {...getInputProps(
          emojiOptionDataTestId ? { "data-testid": emojiOptionDataTestId } : {}
        )}
        title={label}
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          padding: 0,
          margin: "-1px",
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0
        }}
      />
      <Box
        {...getRadioProps()}
        px={4}
        height="auto"
        pb={{ lg: 4 }}
        cursor="pointer"
        display="flex"
        textAlign="center"
        alignItems="center"
        justifyContent="center"
        flexDirection={{ lg: "column" }}
        css={{
          background: "white",
          borderLeftWidth: { lg: 0, sm: "4px" },
          borderLeftStyle: { lg: "initial", sm: "solid" },
          borderLeftColor: "transparent",

          borderBottomWidth: { lg: "4px", sm: 0 },
          borderBottomStyle: { lg: "solid", sm: "initial" },
          borderBottomColor: "transparent",

          "& svg": {
            width: "3rem",
            height: "3rem",
            color: "gray.200"
          },

          "& p": {
            pt: { lg: 3 },
            width: "100%",
            whiteSpace: "normal",
            pl: { base: 3, lg: 0 },
            wordBreak: "break-word",
            display: { xs: "none", sm: "block" }
          },

          _hover: {
            background: "white",
            "@media (hover: hover) and (pointer: fine)": {
              "& svg": {
                color: "yellow.200"
              },
              "& p": {
                fontWeight: "600"
              }
            }
          },

          _focus: {
            boxShadow: "outline",
            background: "white",

            borderTopLeftRadius: { lg: "md", sm: 0 },
            borderTopRightRadius: { sm: "md" },
            borderBottomLeftRadius: { sm: 0 },
            borderBottomRightRadius: { lg: 0, sm: "md" },

            "& svg": {
              color: "yellow.200"
            },
            "& p": {
              fontWeight: "600"
            }
          },

          _checked: {
            background: "white",

            borderLeftColor: { sm: "brand.500", lg: "transparent" },
            borderBottomColor: {
              lg: "brand.500",
              sm: "transparent"
            },

            "& svg": {
              color: "yellow.300"
            },
            "& p": {
              fontWeight: "700"
            }
          },

          _disabled: {
            opacity: 0.4,
            boxShadow: "none",
            cursor: "not-allowed",
            userSelect: "none",

            _hover: {
              "& svg": {
                color: "gray.200"
              },
              "& p": {
                fontWeight: "normal"
              }
            },

            _checked: {
              "& svg": {
                color: "yellow.300"
              },
              "& p": {
                fontWeight: "700"
              }
            }
          }
        }}
      >
        <EmojiIcon emojiType={emojiType} />
        {label && <Text {...labelProps}>{label}</Text>}
      </Box>
    </Box>
  );
};

interface EmojiRadioGroupProps extends TestIdProps {
  id?: string;
  disabled: boolean;
  handleChange: (value: string) => void;
  value: string;
  emojiGroup: {
    type: "VeryUnHappy" | "UnHappy" | "Neutral" | "Happy" | "VeryHappy";
    id: string;
    note: string;
  }[];
  groupProps?: RadioGroupProps;
  groupLayoutProps?: GridProps;
  labelProps?: TextProps;
  radioProps?: RadioProps;
  gap?: number;
  getEmojiOptionDataTestId?: (optionId: string | number) => string | undefined;
}

const EmojiRadioGroup = (props: EmojiRadioGroupProps) => {
  const {
    id,
    handleChange,
    value,
    disabled,
    emojiGroup,
    groupProps,
    groupLayoutProps,
    labelProps,
    radioProps,
    gap,
    getEmojiOptionDataTestId
  } = props;

  const { getRootProps, getRadioProps } = useRadioGroup({
    value,
    onChange: handleChange
  });

  // Explicit getter wins; otherwise derive `option-<id>` from the root id.
  const t = scopedTestIds(resolveTestId(props));
  const resolveOptionDataTestId = (optionId: string | number) =>
    getEmojiOptionDataTestId?.(optionId) ?? t.child(`option-${optionId}`);

  const defaultGroupProps = {
    ...getRootProps(),
    ...groupProps
  };

  return (
    <Box {...(defaultGroupProps as any)} id={id} {...t.props()}>
      <Grid
        w="100%"
        templateColumns={{ lg: `repeat(${emojiGroup.length}, 1fr)` }}
        gap={gap}
        {...groupLayoutProps}
      >
        {emojiGroup.map(({ type, id, note }, index) => {
          return (
            <GridItem
              key={`emoji-option-${id}`}
              display="flex"
              justifyContent={{ lg: "center" }}
            >
              <EmojiRadio
                key={index}
                value={String(id)}
                disabled={disabled}
                label={note}
                emojiType={type}
                labelProps={labelProps}
                emojiOptionDataTestId={resolveOptionDataTestId(id)}
                radioProps={getRadioProps({
                  value: id,
                  disabled: disabled,
                  ...radioProps
                })}
              />
            </GridItem>
          );
        })}
      </Grid>
    </Box>
  );
};

export { EmojiRadio, EmojiRadioGroup, type EmojiRadioProps };
